import React, { useState, useEffect, useCallback } from 'react';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { Grid, GridItem } from '../../../components/Grid';
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  Dropdown,
  NumberInput,
  MessageBox,
  CreateButton,
  ResetButton,
} from '../../../components/FormElements';
import { StyledSpan } from '../../../components/Styled';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import { getReturnUrl, getAmountInWords } from '../../../utils';

const VendorPaymentsCreate = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorsId, setVendorsId] = useState('');
  const [utrNumber, setUtrNumber] = useState(() => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const formatted = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return formatted;
  });
  const [remark, setRemark] = useState('');
  const [transactionsDate, setTransactionsDate] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('');

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const resetForm = () => {
    setVendorsId('');
    setUtrNumber('');
    setRemark('');
    setTransactionsDate('');
    setAmount('');
  };

  const readVendors = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(`/accounts-management/vendors`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILURE) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setVendors(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      vendorsId,
      utrNumber,
      remark,
      transactionsDate,
      amount,
      transactionType,
    };
    API.post('/accounting/vendor-payments', formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          resetForm();
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.CREATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readVendors();
  }, [readVendors]);

  const vendorOptions = [{ title: '-- SELECT VENDOR --', value: '' }];
  vendors.forEach(({ id, firstName, lastName, firmName }) =>
    vendorOptions.push({
      title: `${firstName} ${lastName}`,
      subTitle: firmName,
      value: id,
    })
  );

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method='POST' action='#' onSubmit={onSubmit}>
          <Grid columns={
            'calc(33.33% - 15px) calc(33.33% - 15px) calc(33.33% - 15px)'
          }>
            <Fieldset>
              <Label required>Vendor</Label>
              <Dropdown
                placeholder='Please select vendor'
                value={vendorsId}
                onChange={setVendorsId}
                options={vendorOptions}
                disabled={isLoading}
              />
            </Fieldset>
            <GridItem>
              <Fieldset>
                <Label required>Transaction Type</Label>
                <Dropdown
                  placeholder="Please Transaction Type"
                  value={transactionType}
                  onChange={setTransactionType}
                  options={[
                    {
                      title: "Payment",
                      value: "Credit",
                    },
                    {
                      title: "Stock",
                      value: "Debit",
                    },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Transactions Date</Label>
                <TextInput
                  type='date'
                  value={transactionsDate}
                  onChange={setTransactionsDate}
                  placeholder='Please select transactions date'
                  disabled={isLoading === true}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid
            columns={
              'calc(33.33% - 15px) calc(33.33% - 15px) calc(33.33% - 15px)'
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>UTR Number</Label>
                <TextInput
                  value={utrNumber}
                  onChange={setUtrNumber}
                  placeholder='Please enter UTR number'
                  maxLength={100}
                  disabled={isLoading === true}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Amount</Label>
                <NumberInput
                  value={amount}
                  onChange={setAmount}
                  placeholder='Please enter amount'
                  maxLength={10}
                  disabled={isLoading === true}
                  pattern={`[0-9.]*`}
                />
                {amount && (
                  <StyledSpan style={{ color: '#000' }}>
                    {getAmountInWords(amount)}
                  </StyledSpan>
                )}
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label>Remark</Label>
                <TextInput
                  value={remark}
                  onChange={setRemark}
                  placeholder='Please enter remark'
                  maxLength={100}
                  disabled={isLoading === true}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <CreateButton disabled={isLoading === true} type='submit'>
              Create
            </CreateButton>
            <ResetButton
              disabled={isLoading === true}
              type='button'
              onClick={resetForm}
            >
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(VendorPaymentsCreate);
